/**
 * Командная палитра: ⌘K / Ctrl+K.
 *
 * Разделы, отчёты, пользователи (у администратора), смена периода и города
 * там, где они есть. Только то, что выдано: пункты берутся из той же модели
 * навигации, отчёты — из user.reports, города — из ответа сервера, уже
 * суженного правами. Без зависимостей.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../api'
import type { AdminUser, CurrentUser, DimensionValue } from '../types'
import { formatSelection, labelOf, normalize, parseSelection, shift, isFuture, type Grain } from '../finance/period'
import { Icon, type IconName } from './Icon'
import { navModel, useNavGo } from './nav'
import { useDialog } from './useDialog'
import { usePresence } from '../finance/motion'
import { visibleSections } from '../finance/FinanceBlock'
import { visibleSalesSections } from '../insights/sections'

interface Command {
  key: string
  group: string
  label: string
  hint?: string
  icon: IconName
  keywords?: string
  run: () => void
}

/**
 * Есть ли на текущем экране панель периода и города. Команды «Предыдущий
 * период» и «Город: …» предлагаются только там, где они что-то меняют:
 * на сводке, в отчётах и в админке их нет.
 */
function filtersOn(path: string, user: CurrentUser): { period: boolean; city: boolean } {
  const finance = visibleSections(user).map((section) => section.code as string)
  const contractorOnly = user.reports.length === 0 && user.can_monitor_ingest
  if (path === '/') return finance.length > 0 && !contractorOnly ? { period: true, city: true } : { period: false, city: false }
  const [, block, section] = path.split('/')
  if (block === 'finance' && section && finance.includes(section)) {
    // Долги — на последнюю дату: период к разделу не применяется.
    return { period: section !== 'debts', city: true }
  }
  if (block === 'sales' && section && visibleSalesSections(user).some((item) => item.code === section)) return { period: true, city: true }
  return { period: false, city: false }
}

export function CommandPalette({ user, open, onClose }: { user: CurrentUser; open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const go = useNavGo()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [people, setPeople] = useState<AdminUser[] | null>(null)
  const [branches, setBranches] = useState<DimensionValue[] | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const dialog = useRef<HTMLDivElement>(null)
  const { period: withPeriod, city: withCity } = filtersOn(location.pathname, user)

  useDialog(dialog, { active: open, onClose, initialFocus: input })
  const presence = usePresence(open)

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActive(0)
    if (user.is_admin && !people) api.adminUsers().then((response) => setPeople(response.users)).catch(() => setPeople([]))
    if (withCity && !branches) api.dimensionValues('branch').then((response) => setBranches(response.values)).catch(() => setBranches([]))
  }, [open])

  const commands = useMemo<Command[]>(() => {
    const result: Command[] = []
    const close = (action: () => void) => () => {
      onClose()
      action()
    }

    const params = new URLSearchParams(location.search)
    const setParam = (key: string, value: string | null) => {
      const next = new URLSearchParams(location.search)
      if (value === null) next.delete(key)
      else next.set(key, value)
      navigate(`${location.pathname}?${next.toString()}`, { replace: true })
    }
    if (withPeriod) {
      const selection = parseSelection(params.get('p'))
      result.push({
        key: 'prev',
        group: 'Период и город',
        label: `Предыдущий период: ${labelOf(shift(selection, -1))}`,
        icon: 'chevron-left',
        keywords: 'назад раньше',
        run: close(() => setParam('p', formatSelection(shift(selection, -1)))),
      })
      if (!isFuture(shift(selection, 1))) {
        result.push({
          key: 'next',
          group: 'Период и город',
          label: `Следующий период: ${labelOf(shift(selection, 1))}`,
          icon: 'chevron-right',
          keywords: 'вперёд позже',
          run: close(() => setParam('p', formatSelection(shift(selection, 1)))),
        })
      }
      for (const [grain, title] of [['month', 'Месяц'], ['quarter', 'Квартал'], ['year', 'Год']] as Array<[Grain, string]>) {
        if (grain === selection.grain) continue
        const next = normalize({ grain, start: selection.start })
        result.push({
          key: `grain-${grain}`,
          group: 'Период и город',
          label: `${title}: ${labelOf(next)}`,
          icon: 'debts',
          keywords: 'период длина',
          run: close(() => setParam('p', formatSelection(next))),
        })
      }
    }
    // Один доступный город — выбирать не из чего: «Все доступные» и есть он.
    if (withCity && (branches?.length ?? 0) > 1) {
      const current = params.get('b')
      if (current) {
        result.push({ key: 'city-all', group: 'Период и город', label: user.scope.branch?.allow ? 'Все доступные города' : 'Все города', icon: 'pin', keywords: 'город филиал', run: close(() => setParam('b', null)) })
      }
      for (const branch of branches ?? []) {
        if (branch.guid === current) continue
        result.push({ key: `city-${branch.guid}`, group: 'Период и город', label: `Город: ${branch.name}`, icon: 'pin', keywords: 'город филиал', run: close(() => setParam('b', branch.guid)) })
      }
    }

    for (const group of navModel(user)) {
      for (const item of group.items) {
        result.push({ key: `nav-${item.key}`, group: 'Разделы', label: item.label, hint: group.title, icon: item.icon, run: close(() => go(item)) })
      }
    }

    for (const report of user.reports) {
      result.push({
        key: `report-${report.code}`,
        group: 'Отчёты',
        label: report.title,
        hint: report.category,
        icon: 'reports',
        keywords: report.description,
        run: close(() => navigate(`/reports/${report.code}`)),
      })
    }

    for (const person of people ?? []) {
      result.push({
        key: `user-${person.id}`,
        group: 'Пользователи',
        label: person.full_name,
        hint: person.roles.map((role) => role.title).join(', ') || person.email,
        icon: 'user',
        keywords: `${person.email} ${person.position ?? ''}`,
        run: close(() => navigate(`/admin/users/${person.id}`)),
      })
    }
    return result
  }, [user, people, branches, withPeriod, withCity, location.pathname, location.search, go, navigate, onClose])

  const needle = query.trim().toLowerCase()
  const shown = (needle ? commands.filter((item) => `${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`.toLowerCase().includes(needle)) : commands).slice(0, 60)

  useEffect(() => setActive(0), [needle])
  useEffect(() => {
    list.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  if (!presence.mounted) return null

  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') setActive((value) => Math.min(shown.length - 1, value + 1))
    else if (event.key === 'ArrowUp') setActive((value) => Math.max(0, value - 1))
    else if (event.key === 'Enter') shown[active]?.run()
    else return
    event.preventDefault()
  }

  let lastGroup = ''
  return createPortal(
    <div className={`palette-layer${presence.leaving ? ' palette-layer--leave' : ''}`} onKeyDown={onKey} aria-hidden={presence.leaving || undefined}>
      <div className="palette-scrim" onClick={onClose} aria-hidden="true" />
      <div className="palette" role="dialog" aria-modal="true" aria-label="Быстрый переход" ref={dialog} tabIndex={-1}>
        <label className="palette__search">
          <Icon name="search" />
          <input
            ref={input}
            className="palette__input"
            placeholder={withPeriod ? 'Раздел, отчёт, период, город…' : withCity ? 'Раздел, отчёт, город…' : 'Раздел или отчёт…'}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={shown[active] ? `palette-${shown[active]!.key}` : undefined}
          />
          <kbd className="palette__kbd">Esc</kbd>
        </label>
        <div className="palette__list" id="palette-list" role="listbox" ref={list}>
          {shown.length === 0 && <div className="palette__empty">Ничего не нашлось. Поиск идёт по разделам, отчётам{user.is_admin ? ', пользователям' : ''}{withCity ? ' и городам' : ''}.</div>}
          {shown.map((item, index) => {
            const heading = item.group !== lastGroup ? item.group : null
            lastGroup = item.group
            return (
              <div key={item.key}>
                {heading && <div className="palette__group">{heading}</div>}
                <div
                  id={`palette-${item.key}`}
                  role="option"
                  aria-selected={index === active}
                  data-active={index === active}
                  className={`palette__item${index === active ? ' palette__item--active' : ''}`}
                  onMouseMove={() => setActive(index)}
                  onClick={item.run}
                >
                  <Icon name={item.icon} />
                  <span className="palette__label">{item.label}</span>
                  {item.hint && <span className="palette__hint">{item.hint}</span>}
                </div>
              </div>
            )
          })}
        </div>
        <div className="palette__foot">
          <span><kbd className="palette__kbd">↑</kbd> <kbd className="palette__kbd">↓</kbd> выбрать</span>
          <span><kbd className="palette__kbd">Enter</kbd> открыть</span>
        </div>
      </div>
    </div>,
    document.body,
  )
}
